import { cloneDeep } from 'lodash';
import { Subject, takeUntil } from 'rxjs';

import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { FloatLabelType } from '@angular/material/form-field';

import {
  AutocompleteOption,
  ChipsAutocomplete,
  ChipsServerSideAutocomplete,
} from '../../interfaces';
import { ParentControlErrorStateMatcher } from '../../utilities';
import { AutocompleteComponent } from '../autocomplete/autocomplete.component';
import { ServerSideAutocompleteComponent } from '../server-side-autocomplete/server-side-autocomplete';

@Component({
  selector: 'phoenix-chips',
  templateUrl: './chips.component.html',
  styleUrls: ['./chips.component.scss'],
  standalone: false,
})
export class ChipsComponent implements OnInit, OnChanges, OnDestroy {
  readonly destroyControl$ = new Subject<void>();
  readonly destroy$ = new Subject<void>();
  // ViewChild references
  @ViewChild('chipsAutocomplete') chipsAutocomplete!:
    AutocompleteComponent | ServerSideAutocompleteComponent;
  @ViewChild('chipsContainerElement') chipsContainerElement: any;

  // Input properties
  @Input() title = '';
  @Input() showTitle = true;
  @Input() ariaLabel = '';
  @Input() separatorKeysCodes = ['Enter'];
  @Input() control = new FormControl([] as any[]);
  @Input() validators: ((data: AbstractControl) => ValidationErrors | null)[] =
    [];
  @Input() errorMessages!: any;
  @Input() autocompleteConfig!: ChipsAutocomplete | ChipsServerSideAutocomplete;
  @Input() loadingAutocomplete = false;
  @Input() hasNavigation = false;
  @Input() allowDuplicates = false;
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() displayValueFormat: ((value: any) => string) | undefined = undefined;
  @Input() chipIconText: ((value: any) => string | null) | undefined =
    undefined;
  @Input() chipIconClass = '';
  @Input() chipIconTooltip = '';
  @Input() filterList: any[] = [];
  @Input() isLoading = false;
  @Input() invalidChipIds: string[] = [];
  @Input() hintTemplate?: TemplateRef<unknown>;

  // Output events
  @Output() contentChange = new EventEmitter<any[]>();

  // Component properties
  validatorList: ((data: AbstractControl) => ValidationErrors | null)[] = [];
  matcher: ErrorStateMatcher = new ErrorStateMatcher();
  inputControl = new FormControl('');
  internalInvalidChipIds: string[] = [];

  // Navigation related properties
  navigationValuesIndex = 0;
  navigationValues: any[] = [];
  disabledNextButton = false;

  // Constants
  readonly NAVIGATION_PAGE_SIZE = 500;
  readonly keyCodeMap = {
    Space: ' ',
    Comma: ',',
    Semicolon: ';',
    Enter: '\n',
    Quote: "'",
    Minus: '-',
    Equal: '=',
    Period: '.',
    Slash: '/',
    Backslash: '\\',
    BracketLeft: '[',
    BracketRight: ']',
    Backquote: '`',
  };

  // Validator to prevent duplicate values
  readonly navigationChipsValidators = [
    (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      // Empty values are valid
      if (!value) {
        return null;
      }

      // Check if value already exists either in navigation values or control values
      const isDuplicate = this.hasNavigation
        ? this.navigationValues.some(navValue => navValue == value)
        : false;

      const existsInControl = this.control?.value?.some(
        (controlValue: string | number) => controlValue == value
      );

      return !isDuplicate && !existsInControl
        ? null
        : { alreadyExistsChip: true };
    },
  ];

  readonly navigationChipsErrorMessages = {
    alreadyExistsChip: 'This value already exists in the list',
  };

  /**
   * Initialize the component
   */
  ngOnInit(): void {
    if (this.hasNavigation) {
      this.updateNavigationValues();
    }

    this.setupValidators();
    this.updateInputState();
  }

  /**
   * Handle changes to component inputs
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']) {
      this.updateInputState();
    }
    if (changes['control']) {
      this.destroyControl$.next();

      this.disabled = this.control.disabled;
      this.matcher = new ParentControlErrorStateMatcher(this.control);

      this.control.statusChanges
        .pipe(takeUntil(this.destroyControl$))
        .subscribe(status => {
          if (status === 'DISABLED') {
            this.disabled = true;
          } else {
            this.disabled = false;
          }
        });
    }

    if (changes['invalidChipIds']) {
      this.internalInvalidChipIds = [...this.invalidChipIds];
    }
  }

  /**
   * Setup validators for the input control
   */
  private setupValidators(): void {
    this.validatorList = [...cloneDeep(this.validators)];

    if (!this.allowDuplicates) {
      this.validatorList.push(...this.navigationChipsValidators);

      if (this.errorMessages) {
        this.errorMessages = {
          ...this.errorMessages,
          ...this.navigationChipsErrorMessages,
        };
      }
    }

    this.inputControl.setValidators(this.validatorList);
  }

  /**
   * Update the input control's enabled/disabled state
   */
  private updateInputState(): void {
    this.safelyUpdateForm(() => {
      this.disabled ? this.inputControl.disable() : this.inputControl.enable();
    });
  }

  /**
   * Update the navigation values based on current index
   */
  private updateNavigationValues(): void {
    this.navigationValues =
      this.control.value?.slice(
        this.navigationValuesIndex,
        this.navigationValuesIndex + this.NAVIGATION_PAGE_SIZE
      ) ?? [];

    this.disabledNextButton =
      this.navigationValuesIndex + this.NAVIGATION_PAGE_SIZE >=
      (this.control?.value?.length ?? 0);
  }

  /**
   * Get the autocomplete options list
   */
  get autocompleteList(): (string | AutocompleteOption)[] {
    if ('optionList' in this.autocompleteConfig) {
      return this.autocompleteConfig.optionList ?? [];
    }
    return [];
  }

  /**
   * Type guard to check if config is server-side
   */
  get isServerSideAutocomplete(): boolean {
    return 'serverCallback' in this.autocompleteConfig;
  }

  /**
   * Helper to cast config to server-side for template
   */
  get serverSideConfig(): ChipsServerSideAutocomplete {
    return this.autocompleteConfig as ChipsServerSideAutocomplete;
  }

  get normalAutocompleteConfig(): ChipsAutocomplete {
    return this.autocompleteConfig as ChipsAutocomplete;
  }

  /**
   * Get the first error key from input control errors
   */
  get errorKey(): string {
    return Object.keys(this.inputControl.errors || {})[0];
  }

  /**
   * Get the float label status based on input value and chip count
   */
  get floatLabelStatus(): FloatLabelType {
    const shouldFloat =
      this.inputControl.value ?? (this.control?.value?.length ?? 0) > 0;
    return (shouldFloat ? 'always' : 'never') as FloatLabelType;
  }

  /**
   * Get a RegExp pattern for delimiter characters
   */
  get delimiters(): RegExp {
    const delimiterPattern = this.separatorKeysCodes
      .map(code => {
        const key = this.keyCodeMap[code as keyof typeof this.keyCodeMap];
        return key?.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
      })
      .filter(Boolean)
      .join('|');

    return new RegExp(`[${delimiterPattern}]+`);
  }

  /**
   * Format a chip value for display
   */
  chipDisplay(value: string | number): string {
    if (this.displayValueFormat) {
      return this.displayValueFormat(value);
    }

    return this.chipsAutocomplete
      ? this.chipsAutocomplete.displayValue(value)
      : value.toString();
  }

  /**
   * Reset all chips in the component
   */
  resetChips(): void {
    if (this.control?.value?.length === 0) {
      return;
    }

    this.internalInvalidChipIds = [];
    this.safelyUpdateForm(() => {
      this.control.setValue(this.filterList ?? []);
      this.control.markAsDirty();

      if (this.hasNavigation) {
        this.navigationValues = [];
        this.navigationValuesIndex = 0;
      }

      this.contentChange.emit(this.control?.value ?? []);
    });
  }

  /**
   * Adds a new chip from autocomplete
   */
  addAutocomplete(data: any) {
    if (data) {
      if (!this.allowDuplicates) {
        const exists = this.control?.value?.includes(data);
        if (exists) {
          return;
        }
      }

      const newControlValue = [...(this.control?.value ?? []), data];
      this.control.setValue(newControlValue);
      this.control.markAsDirty();
      this.contentChange.emit(this.control?.value ?? []);
    }
  }

  /**
   * Adds a new chip
   */
  add(event?: KeyboardEvent): void {
    if (this.inputControl.valid) {
      if (event && !this.separatorKeysCodes.includes(event.code)) {
        return;
      }

      const value = (this.inputControl.value || '').trim();

      if (value) {
        this.control.markAsDirty();

        const newControlValue = [...(this.control?.value ?? []), value];
        this.control.setValue(newControlValue);

        if (this.hasNavigation) {
          const newNavigationValues = [...this.navigationValues, value];
          this.navigationValues = newNavigationValues;
        }

        this.contentChange.emit(this.control?.value ?? []);
      }

      this.cleanInput();
    }
  }

  /**
   * Clear the input control
   */
  private cleanInput(): void {
    this.safelyUpdateForm(() => {
      this.inputControl.patchValue('');
    });
  }

  /**
   * Remove a chip with animation support
   */
  remove(number: number, chipValue?: string): void {
    if (this.hasNavigation && chipValue) {
      const controlIndex = (this.control?.value ?? []).indexOf(chipValue);
      const valuesIndex = this.navigationValues.indexOf(chipValue);

      if (valuesIndex > -1) {
        const navigationValues = [...this.navigationValues];
        navigationValues.splice(valuesIndex, 1);
        this.navigationValues = navigationValues;
      }

      if (controlIndex > -1) {
        const controlValue = [...(this.control?.value ?? [])];
        controlValue.splice(controlIndex, 1);
        this.control.setValue(controlValue);
      }
    } else {
      const value = [...(this.control?.value ?? [])];
      value.splice(number, 1);
      this.control.setValue(value);
    }

    this.control.markAsDirty();
    this.contentChange.emit(this.control?.value ?? []);

    if (
      this.hasNavigation &&
      this.navigationValues.length === 0 &&
      this.navigationValuesIndex > 0
    ) {
      this.navigationValuesIndex = Math.max(
        0,
        this.navigationValuesIndex - this.NAVIGATION_PAGE_SIZE
      );
      this.updateNavigationValues();
    }
  }

  /**
   * Handles pasting multiple values
   */
  paste(event: ClipboardEvent) {
    const pastedData = event.clipboardData?.getData('text/plain');
    if (pastedData) {
      const splitData = pastedData
        .split(this.delimiters)
        .filter(data => data.trim() !== '');

      let filteredData = splitData.filter(value => {
        return !this.validatorList.some(validator => {
          const data = validator(new FormControl(value));
          return data !== null && Object.keys(data).length > 0;
        });
      });

      if (!this.allowDuplicates) {
        const currentValues = this.control.value ?? [];

        const existingValuesMap = new Set(
          currentValues.map((val: string | number) =>
            val.toString().toLowerCase()
          )
        );
        const uniqueNewValues = new Set<string>();
        filteredData = filteredData.filter(value => {
          const normalizedValue = value.toLowerCase();
          if (
            existingValuesMap.has(normalizedValue) ||
            uniqueNewValues.has(normalizedValue)
          ) {
            return false;
          }
          uniqueNewValues.add(normalizedValue);
          return true;
        });
      }

      this.cleanInput();
      if (filteredData.length === 0) {
        return;
      }

      this.control.markAsDirty();

      const newValue = [...(this.control?.value ?? []), ...filteredData];
      this.control.setValue(newValue);

      if (this.hasNavigation) {
        this.updateNavigationValues();
      }

      this.contentChange.emit(this.control?.value ?? []);
    }
  }

  /**
   * Navigate to the next page of chips
   */
  onNextData(): void {
    this.navigationValuesIndex += this.NAVIGATION_PAGE_SIZE;
    this.updateNavigationValues();
  }

  /**
   * Navigate to the end of the chips list
   */
  goToEnd(): void {
    const totalItems = this.control?.value?.length ?? 0;
    const remainder = totalItems % this.NAVIGATION_PAGE_SIZE;

    this.navigationValuesIndex = Math.max(
      0,
      totalItems - (remainder || this.NAVIGATION_PAGE_SIZE)
    );

    this.updateNavigationValues();
    this.disabledNextButton = true;
  }

  /**
   * Navigate to the previous page of chips
   */
  onPreviousData(): void {
    this.navigationValuesIndex = Math.max(
      0,
      this.navigationValuesIndex - this.NAVIGATION_PAGE_SIZE
    );
    this.updateNavigationValues();
  }

  /**
   * Navigate to the beginning of the chips list
   */
  goToBeginning(): void {
    this.navigationValuesIndex = 0;
    this.updateNavigationValues();
  }

  /**
   * Get the current range of navigation values being displayed
   */
  getNavigationValuesRange(): string {
    const totalItems = this.control?.value?.length ?? 0;
    const startIndex = this.navigationValuesIndex + 1;
    const endIndex = Math.min(
      this.navigationValuesIndex + this.NAVIGATION_PAGE_SIZE,
      totalItems
    );

    return `${startIndex}-${endIndex}`;
  }

  /**
   * Helper method to safely update form values with proper change detection
   * @param updateFn Function that performs the actual update
   */
  private safelyUpdateForm(updateFn: () => void): void {
    setTimeout(() => {
      updateFn();
    });
  }

  /**
   * Clean up subscriptions when component is destroyed
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroyControl$.next();
    this.destroyControl$.complete();
  }
}
